import { useState } from 'react';
import { useRouter } from 'next/router';

import { Box, Button, FieldSet, FormHelp, Input, Legend, Text, TextButton } from '@zillow/constellation-10';

import {
  useRentalManagerUserEmailVerificationWebApiHandler_validateVerificationCode,
  useRentalManagerUserPhoneVerificationWebApiHandler_validateVerificationCode,
} from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';

import numberUtils from '../../../../utils/numberUtils';

import StringConstants from '../../../../../srcLegacy/shared/constants/StringConstants';
import { VERIFICATION_STEPS } from '../constants';
import { MODES } from '../constants';

import MaxAttemptsError from './MaxAttemptsError';
import VerificationBodyTemplate from './VerificationBodyTemplate';

import type { PhoneVerificationSource } from '@zg-rentals/ts-rental-manager-api-beta-client';
import type { MODES_VALUES } from '../constants';

const VERIFICATION_CODE_LENGTH = 5;

const CONFIG = {
  phone: {
    value: (val: string) => numberUtils.formatPhoneNumberInput(val),
    editButtonText: 'Edit number',
  },
  email: {
    value: (val: string) => val,
    editButtonText: 'Edit email',
  },
};

type Props = {
  contentValue: string;
  currentStepIndex: number;
  incrementStep: (step?: string) => void;
  isContentEditable?: boolean;
  verificationType: string;
  onVerificationConfirmation?: (phoneNumber: string) => Promise<void>;
  verificationSource: PhoneVerificationSource;
  verificationMode?: MODES_VALUES;
};

type ErrorState = { code?: string; message?: string };

const VerificationCodeSent = ({
  contentValue,
  currentStepIndex,
  incrementStep,
  isContentEditable = true,
  onVerificationConfirmation,
  verificationSource,
  verificationMode = MODES.PHONE,
}: Props) => {
  const router = useRouter();
  const listingAlias = router.query.listingAlias as string;

  const { mutateAsync: validatePhoneCode } =
    useRentalManagerUserPhoneVerificationWebApiHandler_validateVerificationCode();
  const { mutateAsync: validateEmailCode } =
    useRentalManagerUserEmailVerificationWebApiHandler_validateVerificationCode();

  const [error, setError] = useState<ErrorState>({});
  const [verificationCode, setVerificationCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const isButtonDisabled = error?.code === StringConstants.SS_ERRORS.attempt_limit_reached;

  const onSuccess = () => {
    if (onVerificationConfirmation) {
      return onVerificationConfirmation(contentValue).then(() => incrementStep());
    }
    incrementStep();
  };

  const onError = (err: { response?: { data?: { message?: string; code?: string } }; message?: string }) => {
    const message = err?.response?.data?.message ?? err?.message ?? 'There was an error verifying your code, try again';
    const code = err?.response?.data?.code;
    setError({ code, message });
  };

  const confirmPhoneVerification = () =>
    validatePhoneCode({ getParams: { phone: contentValue, verificationCode, verificationSource } })
      .then(onSuccess)
      .catch(onError)
      .finally(() => setIsLoading(false));

  const confirmEmailVerification = () =>
    validateEmailCode({ getParams: { listingAlias, email: contentValue, verificationCode } })
      .then(onSuccess)
      .catch(onError)
      .finally(() => setIsLoading(false));

  const confirmVerification = () => {
    if (verificationCode.length < VERIFICATION_CODE_LENGTH) {
      setError({ message: 'Enter a 5 digit verification code' });
      return;
    }
    setIsLoading(true);
    setError({});
    switch (verificationMode) {
      case MODES.PHONE:
        return confirmPhoneVerification();
      case MODES.EMAIL:
        return confirmEmailVerification();
      default:
        return;
    }
  };

  const handleGetNewCodeClick = () => {
    incrementStep(VERIFICATION_STEPS.GET_VERIFICATION_CODE);
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, VERIFICATION_CODE_LENGTH);
    setVerificationCode(value);
    setError({});
    if (value.length === VERIFICATION_CODE_LENGTH) {
      // Allow submit on complete
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && verificationCode.length === VERIFICATION_CODE_LENGTH) {
      confirmVerification();
    }
  };

  const renderErrorMessage = () => {
    const message = error?.message ?? 'There was an error verifying your code, try again';
    if (isButtonDisabled) {
      return <MaxAttemptsError verificationMode={verificationMode} />;
    }
    return (
      <FormHelp error={true} css={{ marginTop: '400', fontSize: 'body-sm' }}>
        {message}
      </FormHelp>
    );
  };

  return (
    <VerificationBodyTemplate
      heading={`Step ${currentStepIndex + 1} - Verification code sent`}
      subheading={
        <Text className="fs-mask">
          We sent the 5 digit code to
          {` ${CONFIG[verificationMode].value(contentValue)}. `}
          {isContentEditable && (
            <TextButton
              tone="brand"
              onClick={() => incrementStep(VERIFICATION_STEPS.ADD_CONTENT)}
              css={{
                fontSize: 'body-sm',
                fontWeight: 400,
                textDecoration: 'underline',
                xl: { fontSize: 'body' },
              }}
            >
              {CONFIG[verificationMode].editButtonText}
            </TextButton>
          )}
        </Text>
      }
    >
      <Box css={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
        <FieldSet
          legend={
            <Legend
              css={{
                textStyle: { base: 'body-sm-bold', lg: 'body-bold' },
                display: 'block',
                marginBottom: { base: '200', lg: '400' },
              }}
            >
              Enter the verification code below
            </Legend>
          }
          error={error.message ? true : false}
          description={error.message ? renderErrorMessage() : undefined}
          css={{ margin: '{spacing.400} auto 0' }}
        >
          <Input
            data-testid="VerificationCodeValidator"
            inputMode="numeric"
            maxLength={VERIFICATION_CODE_LENGTH}
            placeholder="00000"
            value={verificationCode}
            onChange={handleCodeChange}
            onKeyDown={handleKeyDown}
            css={{ width: '120px', margin: '0 auto', display: 'block', textAlign: 'center' }}
            aria-label="Verification code"
          />
        </FieldSet>
        <Box
          css={{
            marginTop: '400',
            marginBottom: '600',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Text textStyle="body-xs">
            Didn&#39;t receive the code?&nbsp;
            <TextButton tone="brand" disabled={isLoading} onClick={handleGetNewCodeClick}>
              <TextButton.Label css={{ textDecoration: 'underline', textStyle: 'body-xs-bold' }}>
                Get new code
              </TextButton.Label>
            </TextButton>
          </Text>
        </Box>
        <Button
          tone="brand"
          emphasis="filled"
          disabled={isButtonDisabled}
          loading={isLoading}
          onClick={confirmVerification}
          css={{ minWidth: '200px', display: 'block', margin: '0 auto' }}
        >
          Check code
        </Button>
      </Box>
    </VerificationBodyTemplate>
  );
};

export default VerificationCodeSent;
